import { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { uniq } from 'lodash';
import { useEventListener } from 'usehooks-ts';
import { Box, Chip } from '@mui/material';

import generateKey from 'helper/generateKey';

const SPACE_KEY_CODE = 32;
const LIST_CHANGE_KEY_CODES = ['Enter'];
const DELETE_KEY_CODES = ['Backspace', 'Delete'];

const styles = {
  container: ({ palette, breakpoints }) => ({
    width: '100%',
    paddingBlock: 1,
    paddingInline: 2,
    display: 'flex',
    alignItems: 'flex-start',
    gap: 1,
    borderRadius: '10px',
    flexWrap: 'wrap',
    border: `2px solid ${palette.colors.darkPale}`,
    backgroundColor: palette.common.white,
    height: 235,
    marginBottom: 5,
    cursor: 'text',
    overflowY: 'auto',
    [breakpoints.up('md')]: {
      height: 167,
      marginBottom: 10,
    },
  }),
  input: ({ palette, typography }) => ({
    ...typography.caption,
    height: 30,
    flex: '1 0 auto',
    border: 'none',
    whiteSpace: 'nowrap',
    color: palette.text.primary,
    '&:focus': {
      outline: 'none',
    },
  }),
  listItem: ({ palette, typography }) => ({
    ...typography.button,
    backgroundColor: palette.colors.frameworkColor,
    color: palette.text.secondary,
    cursor: 'default',
    svg: {
      color: `${palette.text.secondary} !important`,
      opacity: 0.5,
    },
    '&:hover': {
      svg: {
        opacity: 1,
      },
    },
  }),
};

const ListInput = ({
  values,
  onChange,
  validate,
  allowDuplicates = false,
  placeholder = '',
  onInputChange = null,
  maxLength = 256,
  ...inputProps
}) => {
  const [newValue, setNewValue] = useState('');
  const inputRef = useRef(null);

  const valuesWithKeys = values.map((value) => ({
    text: value,
    id: generateKey(),
  }));

  const handleChangeValuesList = (newValues = []) => {
    const formattedValues = allowDuplicates ? newValues : uniq(newValues);

    onChange([...values, ...formattedValues]);
    setNewValue('');
  };

  const handleNewValueChange = (event) => {
    const value = event.target.value.trim();

    setNewValue(value);

    if (typeof onInputChange === 'function') {
      onInputChange(value);
    }
  };

  const handleDeleteItem = (id) => {
    const updatedList = values.filter((_, idx) => idx !== id);

    onChange(updatedList);
  };

  const handleKeyDown = (event) => {
    const { key, target } = event;

    if (LIST_CHANGE_KEY_CODES.includes(event.key)) {
      const isValidInputValue = validate(newValue);

      if (!isValidInputValue) {
        return;
      }

      event.preventDefault();
      handleChangeValuesList([newValue]);
    }

    if (DELETE_KEY_CODES.includes(key) && !target.value) {
      handleDeleteItem(values.length - 1);
    }
  };

  const handlePaste = (event) => {
    event.preventDefault();

    const pastedItems = event.clipboardData
      .getData('text')
      .split(/\s/)
      .filter((item) => validate(item))
      .map((item) => item.substring(0, maxLength));

    handleChangeValuesList(pastedItems);
  };

  const handleBlur = () => {
    const isValidInputValue = validate(newValue);

    if (isValidInputValue) {
      handleChangeValuesList([newValue]);
    }
  };

  const handleTextInput = (event) => {
    const { data, target } = event;
    const lastSymbolKeyCode = data ? data.charCodeAt(0) : NaN;

    if (lastSymbolKeyCode && lastSymbolKeyCode === SPACE_KEY_CODE) {
      const value = target.value.trim();

      if (validate(value)) {
        handleChangeValuesList([value]);
      }
    }
  };

  useEventListener('input', handleTextInput, inputRef);

  return (
    <Box
      sx={styles.container}
      onClick={() => {
        inputRef.current.focus();
      }}
    >
      {valuesWithKeys.map(({ id, text }, index) => (
        <Chip
          key={id}
          label={text}
          id={`list-input-item-${id}`}
          onDelete={() => handleDeleteItem(index)}
          sx={styles.listItem}
        />
      ))}

      <Box
        {...inputProps}
        autoComplete="off"
        component="input"
        enterKeyHint="enter"
        ref={inputRef}
        sx={styles.input}
        placeholder={!values.length ? placeholder : ''}
        value={newValue}
        onChange={handleNewValueChange}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onBlur={handleBlur}
        maxLength={maxLength}
      />
    </Box>
  );
};

ListInput.propTypes = {
  values: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
  validate: PropTypes.func.isRequired,
  allowDuplicates: PropTypes.bool,
  placeholder: PropTypes.string,
  onInputChange: PropTypes.func,
  maxLength: PropTypes.number,
};
export default ListInput;
